import type {
  LocalParticipant,
  LocalTrackPublication,
  TrackPublishOptions,
  VideoCaptureOptions,
  AudioCaptureOptions,
} from 'livekit-client';
import {
  CAMERA_CAPTURE_FALLBACKS,
  MIC_CAPTURE_FALLBACKS,
  isInvalidMediaConstraintError,
  sanitizeAudioCaptureOptions,
  sanitizeVideoCaptureOptions,
} from '@/lib/media-capture-constraints';

const RETRY_DELAYS_MS = [0, 400, 900, 1400];

export type SetCameraEnabledInvoke = (
  enabled: boolean,
  options?: VideoCaptureOptions,
  publishOptions?: TrackPublishOptions,
) => Promise<LocalTrackPublication | undefined>;

export type SetMicrophoneEnabledInvoke = (
  enabled: boolean,
  options?: AudioCaptureOptions,
  publishOptions?: TrackPublishOptions,
) => Promise<LocalTrackPublication | undefined>;

function isRecoverableCameraError(error: unknown): boolean {
  if (error instanceof DOMException) {
    const name = error.name?.toLowerCase() ?? '';
    if (name === 'notallowederror' || name === 'securityerror') {
      return false;
    }
    if (
      name === 'overconstrainederror' ||
      name === 'notreadableerror' ||
      name === 'trackstarterror' ||
      name === 'aborterror' ||
      name === 'typeerror'
    ) {
      return true;
    }
  }
  if (isInvalidMediaConstraintError(error)) {
    return true;
  }
  const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return /allocate video|not readable|device in use|could not start video source/i.test(msg);
}

function isRecoverableMicError(error: unknown): boolean {
  if (error instanceof DOMException) {
    const name = error.name?.toLowerCase() ?? '';
    if (name === 'notallowederror' || name === 'securityerror') {
      return false;
    }
    if (
      name === 'overconstrainederror' ||
      name === 'notreadableerror' ||
      name === 'trackstarterror' ||
      name === 'aborterror' ||
      name === 'typeerror'
    ) {
      return true;
    }
  }
  return isInvalidMediaConstraintError(error);
}

function captureOptionsForCameraAttempt(
  base: VideoCaptureOptions | undefined,
  attempt: number,
): VideoCaptureOptions | undefined {
  const fallback =
    CAMERA_CAPTURE_FALLBACKS[Math.min(attempt, CAMERA_CAPTURE_FALLBACKS.length - 1)];
  const safeBase = sanitizeVideoCaptureOptions(base);

  if (attempt === 0) {
    return safeBase;
  }

  // Tentativas intermediárias: relaxa resolução mantendo device ideal (se houver).
  if (attempt < CAMERA_CAPTURE_FALLBACKS.length - 1) {
    return {
      ...(safeBase ?? {}),
      ...fallback,
      ...(safeBase?.deviceId ? { deviceId: safeBase.deviceId } : {}),
    };
  }

  // Última: sobrescreve deviceId exact/stale com ideal default.
  return { ...fallback };
}

function captureOptionsForMicAttempt(
  base: AudioCaptureOptions | undefined,
  attempt: number,
): AudioCaptureOptions | undefined {
  const fallback = MIC_CAPTURE_FALLBACKS[Math.min(attempt, MIC_CAPTURE_FALLBACKS.length - 1)];
  const safeBase = sanitizeAudioCaptureOptions(base);

  if (attempt === 0) {
    return safeBase;
  }

  if (attempt < MIC_CAPTURE_FALLBACKS.length - 1) {
    return {
      ...(safeBase ?? {}),
      ...fallback,
      ...(safeBase?.deviceId ? { deviceId: safeBase.deviceId } : {}),
    };
  }

  return { ...fallback };
}

/**
 * Retries when enabling — transient getUserMedia / publish failures often recover
 * with a short delay. Em Overconstrained / "Invalid constraint", relaxa resolução
 * e por fim remove deviceId exact (comum com 'default' persistido).
 */
export async function setCameraEnabledWithRetry(
  localParticipant: LocalParticipant,
  enabled: boolean,
  options?: {
    maxAttempts?: number;
    logLabel?: string;
    captureOptions?: VideoCaptureOptions;
    publishOptions?: TrackPublishOptions;
    /** Evita recursão quando setCameraEnabled está patched. */
    invoke?: SetCameraEnabledInvoke;
  },
): Promise<LocalTrackPublication | undefined> {
  const invoke =
    options?.invoke ?? localParticipant.setCameraEnabled.bind(localParticipant);

  if (!enabled) {
    return invoke(false, options?.captureOptions, options?.publishOptions);
  }

  const maxAttempts = options?.maxAttempts ?? CAMERA_CAPTURE_FALLBACKS.length;
  const label = options?.logLabel ?? 'camera';
  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const captureOptions = captureOptionsForCameraAttempt(options?.captureOptions, attempt);
    try {
      const pub = await invoke(true, captureOptions, options?.publishOptions);
      if (attempt > 0) {
        console.info(
          `[${label}] Câmera ativada na tentativa ${attempt + 1}/${maxAttempts}` +
            (captureOptions?.resolution
              ? ` (resolução relaxada ${captureOptions.resolution.width}×${captureOptions.resolution.height})`
              : ' (constraints permissivas)'),
        );
      }
      return pub;
    } catch (error) {
      lastError = error;
      console.warn(
        `[${label}] Falha ao ativar câmera (tentativa ${attempt + 1}/${maxAttempts}):`,
        error,
      );
      if (attempt < maxAttempts - 1 && isRecoverableCameraError(error)) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt] ?? 1500));
        continue;
      }
      break;
    }
  }

  throw lastError;
}

export async function setMicrophoneEnabledWithRetry(
  localParticipant: LocalParticipant,
  enabled: boolean,
  options?: {
    maxAttempts?: number;
    logLabel?: string;
    captureOptions?: AudioCaptureOptions;
    publishOptions?: TrackPublishOptions;
    invoke?: SetMicrophoneEnabledInvoke;
  },
): Promise<LocalTrackPublication | undefined> {
  const invoke =
    options?.invoke ?? localParticipant.setMicrophoneEnabled.bind(localParticipant);

  if (!enabled) {
    return invoke(false, options?.captureOptions, options?.publishOptions);
  }

  const maxAttempts = options?.maxAttempts ?? MIC_CAPTURE_FALLBACKS.length;
  const label = options?.logLabel ?? 'microphone';
  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const captureOptions = captureOptionsForMicAttempt(options?.captureOptions, attempt);
    try {
      const pub = await invoke(true, captureOptions, options?.publishOptions);
      if (attempt > 0) {
        console.info(`[${label}] Microfone ativado na tentativa ${attempt + 1}/${maxAttempts}`);
      }
      return pub;
    } catch (error) {
      lastError = error;
      console.warn(
        `[${label}] Falha ao ativar microfone (tentativa ${attempt + 1}/${maxAttempts}):`,
        error,
      );
      if (attempt < maxAttempts - 1 && isRecoverableMicError(error)) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt] ?? 1500));
        continue;
      }
      break;
    }
  }

  throw lastError;
}
